import { useState, useEffect, useCallback } from 'react';
import { supplierInvoicesService } from '../lib/database';
import { getDataSource } from '../lib/config';
import type { SupplierInvoice } from '../types';

interface UseSupplierInvoicesReturn {
  supplierInvoices: SupplierInvoice[];
  loading: boolean;
  error: string | null;
  create: (invoice: Omit<SupplierInvoice, 'id'>) => Promise<SupplierInvoice>;
  update: (id: string, updates: Partial<SupplierInvoice>) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

export const useSupplierInvoices = (enabled = true): UseSupplierInvoicesReturn => {
  const [supplierInvoices, setSupplierInvoices] = useState<SupplierInvoice[]>([]);
  const [loading, setLoading] = useState(() => enabled && getDataSource() !== 'mock');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    if (getDataSource() === 'mock') {
      setSupplierInvoices([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = supplierInvoicesService.subscribe(
      (invoices) => {
        setSupplierInvoices(invoices);
        setLoading(false);
      },
      (err) => {
        setError(err instanceof Error ? err.message : 'Failed to load supplier invoices');
        setLoading(false);
      },
    );
    return () => unsub();
  }, [enabled]);

  const create = useCallback(async (invoice: Omit<SupplierInvoice, 'id'>) => {
    const created = await supplierInvoicesService.create(invoice);
    setSupplierInvoices((prev) => [created, ...prev.filter((i) => i.id !== created.id)]);
    return created;
  }, []);

  const update = useCallback(async (id: string, updates: Partial<SupplierInvoice>) => {
    await supplierInvoicesService.update(id, updates);
    setSupplierInvoices((prev) =>
      prev.map((inv) => (inv.id === id ? { ...inv, ...updates } : inv)),
    );
  }, []);

  const remove = useCallback(async (id: string) => {
    await supplierInvoicesService.delete(id);
    setSupplierInvoices((prev) => prev.filter((inv) => inv.id !== id));
  }, []);

  return { supplierInvoices, loading, error, create, update, remove };
};
